<template>
  <gas-page-shell :breadcrumb="['告警管理', '历史告警']">
    <el-form :inline="true" :model="query" size="small" class="search-form">
      <el-form-item label="告警时间">
        <el-date-picker
          v-model="dateRange"
          type="daterange"
          range-separator="至"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          value-format="yyyy-MM-dd"
          style="width:240px"
          @change="onDateRangeChange"
        />
      </el-form-item>
      <el-form-item label="告警级别">
        <el-select v-model="query.alarmLevel" clearable placeholder="全部" style="width:120px">
          <el-option label="一般" value="info" />
          <el-option label="重要" value="warning" />
          <el-option label="紧急" value="critical" />
        </el-select>
      </el-form-item>
      <el-form-item label="处理状态">
        <el-select v-model="query.alarmStatus" clearable placeholder="全部" style="width:120px">
          <el-option label="待处理" value="pending" />
          <el-option label="处理中" value="processing" />
          <el-option label="已处理" value="resolved" />
        </el-select>
      </el-form-item>
      <el-form-item label="设备类型">
        <el-select v-model="query.deviceType" clearable placeholder="全部类型" style="width:160px">
          <el-option v-for="t in deviceTypeOptions" :key="t" :label="t" :value="t" />
        </el-select>
      </el-form-item>
      <el-form-item label="设备编号">
        <el-input v-model="query.deviceCode" clearable placeholder="设备/终端编号" style="width:160px" @keyup.enter.native="search" />
      </el-form-item>
      <el-form-item label="关键词">
        <el-input v-model="query.keyword" clearable placeholder="标题/终端编号" style="width:180px" @keyup.enter.native="search" />
      </el-form-item>
      <el-form-item>
        <el-button type="primary" icon="el-icon-search" @click="search">查询</el-button>
        <el-button @click="resetSearch">重置</el-button>
      </el-form-item>
    </el-form>

    <div class="table-title-row">
      <div class="table-title-group">
        <span class="table-title">历史告警 <em>共 {{ total }} 条记录</em></span>
        <gas-table-toolbar
          :columns="tableColumnDefs"
          :visible-map.sync="tableColumnVisible"
          :selected-count="selectedIds.length"
          :show-batch-delete="tableCanBatchDelete"
          @batch-delete="batchRemove"
        />
      </div>
      <el-button type="success" size="small" icon="el-icon-download" :loading="exporting" @click="exportExcel">导出Excel</el-button>
    </div>

    <el-table :data="list" v-loading="loading" border size="small" @selection-change="handleSelectionChange">
      <el-table-column type="selection" width="44" align="center" />
      <el-table-column v-if="colVisible('title')" prop="title" label="告警标题" min-width="160" show-overflow-tooltip />
      <el-table-column v-if="colVisible('deviceType')" label="设备类型" width="110" show-overflow-tooltip>
        <template slot-scope="{ row }">{{ row.deviceType || '—' }}</template>
      </el-table-column>
      <el-table-column v-if="colVisible('deviceCode')" label="设备编号" min-width="120" show-overflow-tooltip>
        <template slot-scope="{ row }">{{ row.deviceCode || row.terminalCode || '—' }}</template>
      </el-table-column>
      <el-table-column v-if="colVisible('terminalCode')" prop="terminalCode" label="终端编号" min-width="120" />
      <el-table-column v-if="colVisible('alarmType')" prop="alarmType" label="告警类型" width="100" />
      <el-table-column v-if="colVisible('alarmLevel')" label="级别" width="88">
        <template slot-scope="{ row }"><gas-status-tag kind="alarmLevel" :value="row.alarmLevel" /></template>
      </el-table-column>
      <el-table-column v-if="colVisible('alarmStatus')" label="状态" width="96">
        <template slot-scope="{ row }"><gas-status-tag kind="alarmStatus" :value="row.alarmStatus" /></template>
      </el-table-column>
      <el-table-column v-if="colVisible('alarmTime')" prop="alarmTime" label="告警时间" min-width="150" />
      <el-table-column v-if="colVisible('resolveTime')" prop="resolveTime" label="处理时间" min-width="150" />
      <el-table-column v-if="colVisible('content')" prop="content" label="告警内容" min-width="160" show-overflow-tooltip />
      <el-table-column label="操作" width="100" fixed="right" align="center">
        <template slot-scope="{ row }">
          <el-button type="text" size="small" @click="openDetail(row)">详情</el-button>
          <el-button type="text" size="small" class="danger" @click="removeRow(row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-pagination
      class="pager"
      background
      layout="total, sizes, prev, pager, next, jumper"
      :total="total"
      :current-page="query.pageNum"
      :page-size="query.pageSize"
      :page-sizes="[10, 20, 50]"
      @size-change="handleSizeChange"
      @current-change="handleCurrentChange"
    />

    <el-dialog title="历史告警详情" :visible.sync="detailVisible" width="560px">
      <el-descriptions v-if="detail" :column="1" border size="small">
        <el-descriptions-item label="告警标题">{{ detail.title }}</el-descriptions-item>
        <el-descriptions-item label="设备类型">{{ detail.deviceType || '—' }}</el-descriptions-item>
        <el-descriptions-item label="设备编号">{{ detail.deviceCode || detail.terminalCode || '—' }}</el-descriptions-item>
        <el-descriptions-item label="终端编号">{{ detail.terminalCode || '—' }}</el-descriptions-item>
        <el-descriptions-item label="告警类型">{{ detail.alarmType || '—' }}</el-descriptions-item>
        <el-descriptions-item label="级别"><gas-status-tag kind="alarmLevel" :value="detail.alarmLevel" /></el-descriptions-item>
        <el-descriptions-item label="状态"><gas-status-tag kind="alarmStatus" :value="detail.alarmStatus" /></el-descriptions-item>
        <el-descriptions-item label="告警时间">{{ detail.alarmTime || '—' }}</el-descriptions-item>
        <el-descriptions-item label="处理时间">{{ detail.resolveTime || '—' }}</el-descriptions-item>
        <el-descriptions-item label="告警内容">{{ detail.content || '—' }}</el-descriptions-item>
      </el-descriptions>
    </el-dialog>
  </gas-page-shell>
</template>

<script>
import GasPageShell from '@/components/GasPageShell.vue'
import gasCrudPage from '@/mixins/gasCrudPage'
import deviceTypeOptionsMixin from '@/mixins/deviceTypeOptions'
import { ALARM_HISTORY_COLUMNS } from '@/config/tableColumnPresets'
import { gasApi } from '@/api/gas'
import { resolveStatus } from '@/utils/statusTag'

export default {
  components: { GasPageShell },
  mixins: [gasCrudPage, deviceTypeOptionsMixin],
  data() {
    return {
      idField: 'alarmId',
      listApi: gasApi.alarm.list,
      saveApi: null,
      removeApi: gasApi.alarm.remove,
      batchRemoveApi: gasApi.alarm.batchRemove,
      dateRange: null,
      detailVisible: false,
      detail: null,
      exporting: false,
      query: {
        pageNum: 1,
        pageSize: 10,
        keyword: '',
        alarmStatus: 'resolved',
        alarmLevel: '',
        deviceType: '',
        deviceCode: '',
        beginDate: '',
        endDate: ''
      }
    }
  },
  created() {
    this.initTableColumns(ALARM_HISTORY_COLUMNS)
    this.loadDeviceTypeOptions()
  },
  methods: {
    buildQuery() {
      const p = { ...this.query }
      Object.keys(p).forEach(k => { if (p[k] === '') p[k] = undefined })
      return p
    },
    async loadData() {
      if (!this.listApi) return
      this.loading = true
      try {
        const res = await this.listApi(this.buildQuery())
        const data = res.data || {}
        this.list = data.records || []
        this.total = Number(data.total || 0)
      } finally {
        this.loading = false
      }
    },
    search() {
      this.query.pageNum = 1
      this.loadData()
    },
    resetSearch() {
      this.dateRange = null
      this.query = {
        pageNum: 1,
        pageSize: this.query.pageSize,
        keyword: '',
        alarmStatus: 'resolved',
        alarmLevel: '',
        deviceType: '',
        deviceCode: '',
        beginDate: '',
        endDate: ''
      }
      this.loadData()
    },
    onDateRangeChange(val) {
      if (val && val.length === 2) {
        this.query.beginDate = val[0]
        this.query.endDate = val[1]
      } else {
        this.query.beginDate = ''
        this.query.endDate = ''
      }
    },
    openDetail(row) {
      this.detail = { ...row }
      this.detailVisible = true
    },
    levelLabel(v) {
      return resolveStatus('alarmLevel', v).label
    },
    statusLabel(v) {
      return resolveStatus('alarmStatus', v === 'active' ? 'pending' : v).label
    },
    async exportExcel() {
      this.exporting = true
      try {
        const params = { ...this.buildQuery() }
        delete params.pageNum
        delete params.pageSize
        const res = await gasApi.alarm.export(params)
        const rows = res.data || []
        if (!rows.length) {
          this.$message.warning('暂无数据可导出')
          return
        }
        const headers = ['告警标题', '设备类型', '设备编号', '终端编号', '告警类型', '级别', '状态', '告警时间', '处理时间', '告警内容']
        const lines = [headers.join(',')]
        const esc = v => {
          const s = v == null || v === '' ? '' : String(v)
          return `"${s.replace(/"/g, '""')}"`
        }
        rows.forEach(r => {
          lines.push([
            esc(r.title),
            esc(r.deviceType),
            esc(r.deviceCode || r.terminalCode),
            esc(r.terminalCode),
            esc(r.alarmType),
            esc(this.levelLabel(r.alarmLevel)),
            esc(this.statusLabel(r.alarmStatus)),
            esc(r.alarmTime),
            esc(r.resolveTime),
            esc(r.content)
          ].join(','))
        })
        const blob = new Blob(['\ufeff' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' })
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = `历史告警_${new Date().toISOString().slice(0, 10)}.csv`
        link.click()
        URL.revokeObjectURL(link.href)
        this.$message.success(`导出成功，共 ${rows.length} 条`)
      } finally {
        this.exporting = false
      }
    }
  }
}
</script>

<style scoped>
.search-form {
  margin-bottom: 8px;
}
.table-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  gap: 12px;
}
.table-title-group {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}
.table-title {
  font-size: 15px;
  font-weight: 600;
  color: #1d2129;
}
.table-title em {
  font-style: normal;
  font-weight: 400;
  font-size: 13px;
  color: #86909c;
  margin-left: 6px;
}
.pager {
  margin-top: 16px;
  text-align: right;
}
.danger {
  color: #f53f3f;
}
</style>
